<template>
  <div class="daily-tracker-container">
    <aside class="tracker-sidebar">
      <div class="sidebar-section">
        <h4>Source Country</h4>
        <div class="country-list">
          <div v-for="country in sourceCountries" :key="country.countryid" class="country-item">
            <input
              type="checkbox"
              :id="'src-' + country.countryid"
              :value="country.countryid"
              v-model="selectedSourceCountries"
              @change="fetchData"
            />
            <label :for="'src-' + country.countryid">
              <img :src="getFlagUrl(country.flagIso)" :alt="country.flagIso" class="country-flag" />
              {{ country.displayIso }}
            </label>
          </div>
        </div>
      </div>
      <div class="sidebar-section">
        <h4>Destination Country</h4>
        <div class="country-list">
          <div v-for="country in destinationCountries" :key="country.countryid" class="country-item">
            <input
              type="checkbox"
              :id="'dest-' + country.countryid"
              :value="country.countryid"
              v-model="selectedDestinationCountries"
              @change="fetchData"
            />
            <label :for="'dest-' + country.countryid">
              <img :src="getFlagUrl(country.flagIso)" :alt="country.flagIso" class="country-flag" />
              {{ country.displayIso }}
            </label>
          </div>
        </div>
      </div>
    </aside>
    <main class="tracker-main">
      <div class="tracker-panels">
        <!-- Daily Transaction Volume Panel -->
        <div class="tracker-panel">
          <div class="panel-header">Daily Transaction Volume</div>
          <div class="panel-chart-avg-outside" v-if="chartData && !loading">
  Average: ${{ chartData.avgVolume.toLocaleString() }}
</div>
          <div class="panel-chart">
            <div style="position: absolute; top: 8px; left: 8px; z-index: 2;">
              <select v-model="volumeChartType" style="padding: 2px 8px; border-radius: 6px; border: 1px solid #bbb;">
                <option value="line">Line</option>
                <option value="bar">Bar</option>
                <option value="pie">Pie</option>
              </select>
            </div>
            <!-- Finance-Themed Skeleton Loader for Chart -->
            <div v-if="loading" class="skeleton-finance-chart">
              <div class="skeleton-bar" v-for="i in 8" :key="i" :style="{ height: (40 + Math.random() * 80) + 'px', left: (i * 10 + 10) + '%' }"></div>
              <div class="skeleton-dollar">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill="#e3f0dd"/>
                  <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="20" font-family="Arial" dy=".3em">$</text>
                </svg>
              </div>
            </div>
            <v-chart
              v-else-if="chartData && chartData.dailyVolumeSeries"
              :option="getVolumeChartOption"
              autoresize
              style="width: 100%; height: 320px;"
            />
            <img v-else src="https://dummyimage.com/600x180/ffffff/4caf50.png&text=Volume+Chart" alt="Volume Chart" class="chart-img" />
            
          </div>
          <div class="panel-grid">
            <div class="panel-cell" v-for="(label, idx) in ['Today', 'This week', 'Month-to-date', 'Average Per Transaction<br/>(This Month)']" :key="label">
              <div class="panel-cell-label" v-html="label"></div>
              <div v-if="loading" class="skeleton-finance-amount-bar-wrap">
                <span class="skeleton-finance-icon">
                  <svg v-if="idx < 3" width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <circle cx="11" cy="11" r="11" fill="#e3f0dd"/>
                    <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="14" font-family="Arial" dy=".3em">
                      {{ idx === 2 ? 'Σ' : '$' }}
                    </text>
                  </svg>
                  <svg v-else width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <circle cx="11" cy="11" r="11" fill="#e3f0dd"/>
                    <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="14" font-family="Arial" dy=".3em">#</text>
                  </svg>
                </span>
                <span class="skeleton-finance-amount-bar"></span>
              </div>
              <template v-else>
                <div class="panel-cell-value" v-if="idx === 0">${{ summary.volume.today.toLocaleString() }}</div>
                <div class="panel-cell-value" v-else-if="idx === 1">${{ summary.volume.thisWeek.toLocaleString() }}</div>
                <div class="panel-cell-value" v-else-if="idx === 2">${{ summary.volume.mtd.toLocaleString() }}</div>
                <div class="panel-cell-value" v-else>${{ summary.volume.avgPerTxMtd.toLocaleString() }}</div>
                <div class="panel-cell-sub red" v-if="idx === 0">{{ summary.volume.vsYesterday.label }}</div>
                <div class="panel-cell-sub red" v-else-if="idx === 1">{{ summary.volume.vsPrevWeek.label }}</div>
                <div class="panel-cell-sub red" v-else-if="idx === 2">{{ summary.volume.vsPrevMonth.label }}</div>
              </template>
            </div>
          </div>
        </div>
        <!-- Daily Transaction Numbers Panel -->
        <div class="tracker-panel">
          <div class="panel-header">Daily Transaction Numbers</div>
          <div class="panel-chart-avg-outside" v-if="chartData && !loading">
  Average: {{ chartData.avgCount.toLocaleString() }}
</div>

          <div class="panel-chart">
            <div style="position: absolute; top: 8px; left: 8px; z-index: 2;">
              <select v-model="countChartType" style="padding: 2px 8px; border-radius: 6px; border: 1px solid #bbb;">
                <option value="line">Line</option>
                <option value="bar">Bar</option>
                <option value="pie">Pie</option>
              </select>
            </div>
            <div v-if="loading" class="skeleton-finance-chart">
              <div class="skeleton-bar" v-for="i in 8" :key="i" :style="{ height: (40 + Math.random() * 80) + 'px', left: (i * 10 + 10) + '%' }"></div>
              <div class="skeleton-dollar">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill="#e3f0dd"/>
                  <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="20" font-family="Arial" dy=".3em">#</text>
                </svg>
              </div>
            </div>
            <v-chart
              v-else-if="chartData && chartData.dailyCountSeries"
              :option="getCountChartOption"
              autoresize
              style="width: 100%; height: 320px;"
            />
            <img v-else src="https://dummyimage.com/600x180/ffffff/4caf50.png&text=Numbers+Chart" alt="Numbers Chart" class="chart-img" />
            
          </div>
          <div class="panel-grid">
            <div class="panel-cell" v-for="(label, idx) in ['Today', 'This week', 'Month-to-date', 'Average Daily Number of Transactions<br/>(This Month)']" :key="label">
              <div class="panel-cell-label" v-html="label"></div>
              <div v-if="loading" class="skeleton-finance-amount-bar-wrap">
                <span class="skeleton-finance-icon">
                  <svg v-if="idx < 3" width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <circle cx="11" cy="11" r="11" fill="#e3f0dd"/>
                    <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="14" font-family="Arial" dy=".3em">
                      {{ idx === 2 ? 'Σ' : '#' }}
                    </text>
                  </svg>
                  <svg v-else width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <circle cx="11" cy="11" r="11" fill="#e3f0dd"/>
                    <text x="50%" y="55%" text-anchor="middle" fill="#4caf50" font-size="14" font-family="Arial" dy=".3em">#</text>
                  </svg>
                </span>
                <span class="skeleton-finance-amount-bar"></span>
              </div>
              <template v-else>
                <div class="panel-cell-value" v-if="idx === 0">{{ summary.count.today.toLocaleString() }}</div>
                <div class="panel-cell-value" v-else-if="idx === 1">{{ summary.count.thisWeek.toLocaleString() }}</div>
                <div class="panel-cell-value" v-else-if="idx === 2">{{ summary.count.mtd.toLocaleString() }}</div>
                <div class="panel-cell-value" v-else>{{ summary.count.avgDailyMtd.toLocaleString() }}</div>
                <div class="panel-cell-sub red" v-if="idx === 0">{{ summary.count.vsYesterday.label }}</div>
                <div class="panel-cell-sub red" v-else-if="idx === 1">{{ summary.count.vsPrevWeek.label }}</div>
                <div class="panel-cell-sub red" v-else-if="idx === 2">{{ summary.count.vsPrevMonth.label }}</div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { fetchDailyTransactionTracker, fetchDailyTransactionCountries } from "@/services/dailyTransactionService";
import VueECharts from 'vue-echarts';
import 'echarts';

export default {
  name: "DailyTransactionTracker",
  components: {
    'v-chart': VueECharts
  },
  data() {
    return {
      sourceCountries: [],
      destinationCountries: [],
      selectedSourceCountries: [],
      selectedDestinationCountries: [],
      chartData: null,
      summary: {
        volume: {
          today: 0, thisWeek: 0, mtd: 0, avgPerTxMtd: 0,
          vsYesterday: { label: "" }, vsPrevWeek: { label: "" }, vsPrevMonth: { label: "" }
        },
        count: {
          today: 0, thisWeek: 0, mtd: 0, avgDailyMtd: 0,
          vsYesterday: { label: "" }, vsPrevWeek: { label: "" }, vsPrevMonth: { label: "" }
        }
      },
      loading: false,
      volumeChartType: "line",
      countChartType: "line"
    };
  },
  computed: {
    getVolumeChartOption() {
      if (!this.chartData || !this.chartData.dailyVolumeSeries) return {};
      // Filter for current year and up to current month
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // 1-based
      // Aggregate by month for line/bar chart
      const monthMap = {};
      this.chartData.dailyVolumeSeries.forEach(item => {
        const [year, month] = item.date.split('-');
        if (Number(year) === currentYear && Number(month) <= currentMonth) {
          const key = `${year}-${month}`;
          if (!monthMap[key]) monthMap[key] = 0;
          monthMap[key] += item.value !== undefined ? item.value : item.volume;
        }
      });
      const seriesData = Object.keys(monthMap)
        .sort()
        .map(key => ({
          date: `${key}-01`,
          value: monthMap[key]
        }));

      // Pie chart data: sum by month
      const pieData = [];
      const pieMonthMap = {};
      for (const item of seriesData) {
        const [year, month] = item.date.split('-');
        const key = `${year}-${month}`;
        if (!pieMonthMap[key]) pieMonthMap[key] = 0;
        pieMonthMap[key] += item.value;
      }
      for (const key of Object.keys(pieMonthMap)) {
        const [year, month] = key.split('-');
        pieData.push({
          name: `${new Date(`${year}-${month}-01`).toLocaleString('en-US', { month: 'short', year: 'numeric' })}`,
          value: pieMonthMap[key]
        });
      }

      let chartType = this.volumeChartType;
      if (chartType === "area") chartType = "line";
 if (chartType === "pie") {

  return {
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: "#fff",
      borderColor: "#4caf50",
      borderWidth: 2,
      textStyle: { color: "#222", fontWeight: 600, fontSize: 14 },
      formatter(params) {
        const fmt = v =>
          Math.abs(v) >= 1e9 ? (v/1e9).toFixed(2)+'B' :
          Math.abs(v) >= 1e6 ? (v/1e6).toFixed(2)+'M' :
          Math.abs(v) >= 1e3 ? (v/1e3).toFixed(2)+'k' : v;
        return `<b>${params.name}</b>: <b>${fmt(params.value)}</b> <span style="color:#888">(${params.percent.toFixed(1)}%)</span>`;
      }
    },

    legend: {
      bottom: 0,
      left: "center",
      orient: "horizontal",
      type: "plain",       // <-- IMPORTANT: prevents scrolling
      itemWidth: 12,
      itemHeight: 10,
      padding: [10, 10],
      textStyle: { fontSize: 11, color: "#222" },
      itemGap: 14,
      // allow wrapping on smaller screens
      width: "95%",
      data: pieData.map(i => i.name)
    },

    series: [{
      name: "Monthly Volume",
      type: "pie",
      radius: ["35%", "55%"], // reduced to make room for legend
      center: ["50%", "46%"], // move pie upward to free space

      avoidLabelOverlap: true,

      minShowLabelAngle: 6,

      labelLayout: {
        hideOverlap: true
      },

      label: {
        show: true,
        position: "outside",
        fontSize: 11,
        color: "#222",
        formatter(p) {
          const fmt = v =>
            Math.abs(v) >= 1e9 ? (v/1e9).toFixed(1)+'B' :
            Math.abs(v) >= 1e6 ? (v/1e6).toFixed(1)+'M' :
            Math.abs(v) >= 1e3 ? (v/1e3).toFixed(1)+'k' : v;

          if (p.percent < 2) return ""; // hide tiny labels

          return `${p.name}: ${fmt(p.value)}`;
        }
      },

      labelLine: {
        show: true,
        length: 10,
        length2: 10,
        lineStyle: { color: "#888", width: 1.2 }
      },

      itemStyle: {
        borderColor: "#fff",
        borderWidth: 2,
        shadowBlur: 5,
        shadowColor: "rgba(0,0,0,0.08)"
      },

      data: pieData  // <-- NO grouping, show ALL months
    }]
  };
}





      return {
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          borderColor: '#4caf50',
          borderWidth: 2,
          textStyle: { color: '#222', fontWeight: 600, fontSize: 16 },
          formatter: function(params) {
            let s = `<div style="font-weight:700;font-size:16px;">${params[0].axisValueLabel}</div>`;
            params.forEach(p => {
              s += `<div>
                <span style="display:inline-block;margin-right:8px;border-radius:4px;width:12px;height:12px;background:${p.color};"></span>
                <span style="font-weight:600;">${p.seriesName || 'Volume'}:</span>
                <span style="margin-left:4px;">$${p.value.toLocaleString()}</span>
              </div>`;
            });
            return s;
          }
        },
        legend: {
          show: true,
          top: 16,
          right: 24,
          orient: 'horizontal',
          itemWidth: 18,
          itemHeight: 10,
          textStyle: {
            fontSize: 13,
            fontWeight: 400,
            color: "#444"
          },
          data: ['Volume']
        },
        grid: {
          left: 80,
          right: 28,
          top: 30,
          bottom: 32
        },
        xAxis: {
          type: 'category',
          data: seriesData.map(item => item.date),
          boundaryGap: chartType === "bar",
          name: "Month",
          nameLocation: "middle",
          nameGap: 32,
          nameTextStyle: {
            fontSize: 18,
            fontWeight: 700,
            color: "#222"
          },
          axisLabel: {
            fontSize: 12,
            fontWeight: 400,
            color: "#222",
            margin: 6,
            align: "center",
            interval: 0,
            showMinLabel: true,
            showMaxLabel: true,
            rotate: 0,
            hideOverlap: false,
            show: true,
            formatter: function (value) {
              const d = new Date(value);
              return d.toLocaleString('en-US', { month: 'short' });
            }
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: "#222",
              width: 3
            }
          },
          axisTick: {
            show: true,
            length: 8,
            lineStyle: {
              color: "#222",
              width: 2
            }
          }
        },
        yAxis: {
          type: 'value',
          name: "",
          min: 0,
          axisLabel: {
            fontSize: 12,
            fontWeight: 400,
            color: "#222",
            margin: 10,
            align: "right",
            formatter: function (value) {
              if (value === 0) return "0";
              // Format as 1.2K, 1.2M, 1.2B, etc.
              if (Math.abs(value) >= 1e9) return '$' + (value / 1e9).toFixed(2) + 'B';
              if (Math.abs(value) >= 1e6) return '$' + (value / 1e6).toFixed(2) + 'M';
              if (Math.abs(value) >= 1e3) return '$' + (value / 1e3).toFixed(1) + 'K';
              return '$' + value.toLocaleString();
            }
          },
          splitLine: {
            lineStyle: {
              color: "#f0f2f5",
              type: "dashed"
            }
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: "#222",
              width: 3
            }
          },
          axisTick: {
            show: true,
            length: 8,
            lineStyle: {
              color: "#222",
              width: 2
            }
          }
        },
        series: [{
          name: 'Volume',
          data: seriesData.map(item => item.value),
          type: chartType,
          areaStyle: this.volumeChartType === "area" ? { color: "rgba(76,175,80,0.15)" } : undefined,
          smooth: false,
          itemStyle: { color: '#4caf50' },
          lineStyle: { color: '#4caf50', width: 4 },
          barWidth: '60%',
          symbol: "circle",
          symbolSize: 10,
          label: {
            show: false
          },
          emphasis: {
            focus: 'series'
          }
        }]
      };
    },
    getCountChartOption() {
      if (!this.chartData || !this.chartData.dailyCountSeries) return {};
      // Filter for current year and up to current month
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      // Aggregate by month for line/bar chart
      const monthMap = {};
      this.chartData.dailyCountSeries.forEach(item => {
        const [year, month] = item.date.split('-');
        if (Number(year) === currentYear && Number(month) <= currentMonth) {
          const key = `${year}-${month}`;
          if (!monthMap[key]) monthMap[key] = 0;
          monthMap[key] += item.value !== undefined ? item.value : item.count;
        }
      });
      const seriesData = Object.keys(monthMap)
        .sort()
        .map(key => ({
          date: `${key}-01`,
          value: monthMap[key]
        }));

      // Pie chart data: sum by month
      const pieData = [];
      const pieMonthMap = {};
      for (const item of seriesData) {
        const [year, month] = item.date.split('-');
        const key = `${year}-${month}`;
        if (!pieMonthMap[key]) pieMonthMap[key] = 0;
        pieMonthMap[key] += item.value;
      }
      for (const key of Object.keys(pieMonthMap)) {
        const [year, month] = key.split('-');
        pieData.push({
          name: `${new Date(`${year}-${month}-01`).toLocaleString('en-US', { month: 'short', year: 'numeric' })}`,
          value: pieMonthMap[key]
        });
      }

      let chartType = this.countChartType;
      if (chartType === "area") chartType = "line";
      if (chartType === "pie") {
  return {
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: "#fff",
      borderColor: "#1976d2",
      borderWidth: 2,
      textStyle: { color: "#222", fontSize: 14 }
    },

    legend: {
      bottom: 0,
      left: "center",
      width: "96%",
      orient: "horizontal",
      type: "plain",
      itemWidth: 12,
      itemHeight: 10,
      itemGap: 10,
      textStyle: { fontSize: 11, color: "#222" }
    },

    series: [
      {
        name: "Monthly Count",
        type: "pie",

        // ⭐ Safe & reliable donut size
        radius: ["34%", "50%"],

        // ⭐ Move slightly upward
        center: ["50%", "44%"],

        avoidLabelOverlap: true,

        labelLayout: {
          hideOverlap: true,
          moveOverlap: "shiftY"
        },

        label: {
          show: true,
          fontSize: 11,
          fontWeight: 400,
          alignTo: "labelLine",
          width: 130,
          overflow: "break",

          // ⭐ Add vertical padding to separate labels better
          padding: [2, 0, 2, 0],

          formatter(p) {
            const fmt = (v) =>
              Math.abs(v) >= 1e9
                ? (v / 1e9).toFixed(2) + "B"
                : Math.abs(v) >= 1e6
                ? (v / 1e6).toFixed(2) + "M"
                : Math.abs(v) >= 1e3
                ? (v / 1e3).toFixed(2) + "k"
                : v.toFixed(0);

            // ⭐ Hides tiny slices but keeps everything clean
            if (p.percent < 1.2) return "";

            return `${p.name}: ${fmt(p.value)}`;
          }
        },

        labelLine: {
          show: true,
          length: 12,
          length2: 10,
          minTurnAngle: 110,
          lineStyle: { color: "#666", width: 1.2 }
        },

        itemStyle: {
          borderColor: "#fff",
          borderWidth: 2,
          shadowBlur: 6,
          shadowColor: "rgba(25,118,210,0.1)"
        },

        data: pieData.sort((a, b) => a.name.localeCompare(b.name))
      }
    ]
  };
}



      return {
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          borderColor: '#1976d2',
          borderWidth: 2,
          textStyle: { color: '#222', fontWeight: 600, fontSize: 16 },
          formatter: function(params) {
            let s = `<div style="font-weight:700;font-size:16px;">${params[0].axisValueLabel}</div>`;
            params.forEach(p => {
              s += `<div>
                <span style="display:inline-block;margin-right:8px;border-radius:4px;width:12px;height:12px;background:${p.color};"></span>
                <span style="font-weight:600;">${p.seriesName || 'Count'}:</span>
                <span style="margin-left:4px;">${p.value.toLocaleString()}</span>
              </div>`;
            });
            return s;
          }
        },
        legend: {
          show: true,
          top: 16,
          right: 24,
          orient: 'horizontal',
          itemWidth: 18,
          itemHeight: 10,
          textStyle: {
            fontSize: 13,
            fontWeight: 400,
            color: "#444"
          },
          data: ['Count']
        },
        grid: {
          left: 80,
          right: 28,
          top: 30,
          bottom: 32
        },
        xAxis: {
          type: 'category',
          data: seriesData.map(item => item.date),
          boundaryGap: chartType === "bar",
          name: "Month",
          nameLocation: "middle",
          nameGap: 32,
          nameTextStyle: {
            fontSize: 18,
            fontWeight: 700,
            color: "#222"
          },
          axisLabel: {
            fontSize: 12,
            fontWeight: 400,
            color: "#222",
            margin: 6,
            align: "center",
            interval: 0,
            showMinLabel: true,
            showMaxLabel: true,
            rotate: 0,
            hideOverlap: false,
            show: true,
            formatter: function (value) {
              const d = new Date(value);
              return d.toLocaleString('en-US', { month: 'short' });
            }
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: "#222",
              width: 3
            }
          },
          axisTick: {
            show: true,
            length: 8,
            lineStyle: {
              color: "#222",
              width: 2
            }
          }
        },
        yAxis: {
          type: 'value',
          name: "",
          min: 0,
          axisLabel: {
            fontSize: 12,
            fontWeight: 400,
            color: "#222",
            margin: 10,
            align: "right",
            formatter: function (value) {
              if (value === 0) return "0";
              // Format as 1.2K, 1.2M, 1.2B, etc.
              if (Math.abs(value) >= 1e9) return (value / 1e9).toFixed(2) + 'B';
              if (Math.abs(value) >= 1e6) return (value / 1e6).toFixed(2) + 'M';
              if (Math.abs(value) >= 1e3) return (value / 1e3).toFixed(1) + 'K';
              return value.toLocaleString();
            }
          },
          splitLine: {
            lineStyle: {
              color: "#f0f2f5",
              type: "dashed"
            }
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: "#222",
              width: 3
            }
          }
        },
        series: [{
          name: 'Count',
          data: seriesData.map(item => item.value),
          type: chartType,
          areaStyle: this.countChartType === "area" ? { color: "rgba(25,118,210,0.15)" } : undefined,
          smooth: false,
          itemStyle: { color: '#1976d2' },
          lineStyle: { color: '#1976d2', width: 4 },
          barWidth: '60%',
          symbol: "circle",
          symbolSize: 10,
          label: {
            show: false
          },
          emphasis: {
            focus: 'series'
          }
        }]
      };
    }
  },
  watch: {
    volumeChartType() {
      this.$forceUpdate();
    },
    countChartType() {
      this.$forceUpdate();
    }
  },
  async mounted() {
    await this.fetchCountries();
    await this.fetchData();
  },
  methods: {
    getFlagUrl(iso) {
      if (!iso) return '';
      const code = String(iso).toLowerCase();
      return `https://flagcdn.com/24x18/${code}.png`;
    },
    async fetchCountries() {
      const countries = await fetchDailyTransactionCountries();

      const mapGBtoUK = (iso) => (iso === 'GB' ? 'UK' : iso);

      // backend returns these flags as 0/1 (numbers) in this endpoint
      const isTruthyFlag = (v) => v === true || v === 1 || v === "1";
      const isNotBlacklisted = (c) => !isTruthyFlag(c?.isblacklisted);

      // SOURCE: crossborder flag enabled (per requirement)
      this.sourceCountries = countries
        .filter((c) => isNotBlacklisted(c) && isTruthyFlag(c.crossborder))
        .map((c) => ({
          ...c,
          // keep original ISO for flag, but display UK instead of GB
          flagIso: c.isocode,
          displayIso: mapGBtoUK(c.isocode),
        }))
        .sort((a, b) => String(a.displayIso || "").localeCompare(String(b.displayIso || "")));

      // DESTINATION: destination flag enabled (per requirement)
      this.destinationCountries = countries
        .filter((c) => isNotBlacklisted(c) && isTruthyFlag(c.destination))
        .map((c) => ({
          ...c,
          flagIso: c.isocode,
          displayIso: mapGBtoUK(c.isocode),
        }))
        .sort((a, b) => String(a.displayIso || "").localeCompare(String(b.displayIso || "")));
    },
    async fetchData() {
      this.loading = true;
      const params = {};
      if (this.selectedSourceCountries.length)
        params.sourceCountry = this.selectedSourceCountries.join(",");
      if (this.selectedDestinationCountries.length)
        params.destinationCountry = this.selectedDestinationCountries.join(",");
      const data = await fetchDailyTransactionTracker(params);
      this.chartData = data.chart;
      this.summary = data.summary;
      this.loading = false;
    }
  }
};
</script>

<style scoped>
/* Modern, clean, professional look */
body {
  background: #f4f8fb;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  color: #1a2233;
}
.daily-tracker-container {
  display: flex;
  flex-direction: row;
  min-height: 80vh;
  background: #e3f0dd;
  gap: 0;
  padding: 0;
  border-radius: 18px;
}
.tracker-sidebar {
  width: 220px;
  min-width: 200px;
  max-width: 260px;
  background: #2d3a4a;
  padding: 24px 12px 24px 12px;
  border-radius: 18px 0 0 18px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  color: #fff;
  font-size: 1.08em;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  z-index: 2;
}
.sidebar-section {
  margin-bottom: 24px;
}
.sidebar-section h4 {
  font-size: 1.1em;
  font-weight: 700;
  margin-bottom: 10px;
  color: #e3f0dd;
  border-bottom: 2px solid #a3c7a6;
  padding-bottom: 4px;
}
.country-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.country-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1em;
}
.country-flag {
  width: 16px;
  height: 12px;
  object-fit: contain;
  margin-right: 6px;
  vertical-align: middle;
}
input[type="checkbox"] {
  accent-color: #4caf50;
  width: 16px;
  height: 16px;
}
.tracker-main {
  flex: 1 1 0%;
  min-width: 0;
  padding: 32px 24px 32px 24px;
  background: #e3f0dd;
  border-radius: 0 18px 18px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
}
.tracker-panels {
  display: flex;
  flex-direction: row;
  gap: 32px;
  width: 100%;
  justify-content: center;
}
.tracker-panel {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  padding: 18px 24px 24px 24px;
  min-width: 480px;
  max-width: 700px;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 18px;
  background: #f8fafc;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  border: 1.5px solid #e0e7ef;
  transition: box-shadow 0.2s, border-radius 0.2s, min-width 0.2s, max-width 0.2s;
}
.panel-header {
  font-size: 1.35em;
  font-weight: 700;
  text-align: center;
  margin-bottom: 8px;
  text-decoration: underline;
}
.panel-chart {
  width: 100%;
  height: 340px;
  background: #fff;
  border: 2px solid #222;
  border-radius: 18px;
  margin-bottom: 18px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  padding-top: 8px;
  padding-bottom: 8px;
  transition: box-shadow 0.2s, border-radius 0.2s, height 0.2s;
  overflow: hidden;
}

.v-chart {
  /* Ensure ECharts canvas fills the area */
  width: 100% !important;
  height: 320px !important;
  min-height: 320px !important;
  max-height: 320px !important;
  margin: 0 auto;
  display: block;
}
.chart-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
}
.panel-chart-avg {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 1em;
  color: #222;
  font-weight: 600;
  background: #fff;
  padding: 2px 8px;
  border-radius: 8px;
  border: 1px dashed #888;
  z-index: 2;
  transition: top 0.2s, bottom 0.2s;
}
.panel-chart-avg-top {
  top: 12px;
  bottom: auto;
  right: 18px;
  left: auto;
}
.panel-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 0;
  width: 100%;
  margin-top: 8px;
}
.panel-cell {
  border: 1px solid #bbb;
  border-top: none;
  border-left: none;
  padding: 18px 8px 12px 8px;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.panel-cell-label {
  font-size: 1.08em;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: center;
  text-decoration: underline;
}
.panel-cell-value {
  font-size: 2em;
  font-weight: 700;
  color: #222;
  margin-bottom: 2px;
  text-align: center;
}
.panel-cell-sub {
  font-size: 1.1em;
  font-weight: 600;
  margin-top: 2px;
  text-align: center;
}
.panel-chart-avg-outside {
  font-size: 1em;
  color: #222;
  font-weight: 600;
  background: #fff;
  padding: 2px 10px;
  border-radius: 8px;
  border: 1px dashed #888;
  display: inline-block;
  margin-bottom: 6px;
}

.red {
  color: #e53935;
}
@media (max-width: 1200px) {
  .tracker-panels {
    flex-direction: column;
    gap: 24px;
    align-items: center;
  }
  .tracker-panel {
    min-width: 320px;
    max-width: 98vw;
  }
}
/* Finance Skeleton Loader Styles */
.skeleton-finance-chart {
  width: 100%;
  height: 180px;
  margin-bottom: 12px;
  background: linear-gradient(90deg, #e3f0dd 25%, #f3f3f3 50%, #e3f0dd 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite linear;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}
.skeleton-bar {
  position: absolute;
  bottom: 0;
  width: 8%;
  background: linear-gradient(180deg, #b2dfdb 0%, #4caf50 100%);
  border-radius: 6px 6px 0 0;
  opacity: 0.7;
  animation: skeleton-bar-pulse 1.2s infinite alternate;
}
.skeleton-dollar {
  position: absolute;
  left: 12px;
  top: 12px;
  opacity: 0.7;
  animation: skeleton-dollar-pulse 1.2s infinite alternate;
}
@keyframes skeleton-bar-pulse {
  0% { opacity: 0.5; }
  100% { opacity: 1; }
}
@keyframes skeleton-dollar-pulse {
  0% { transform: scale(1); }
  100% { transform: scale(1.08); }
}
.skeleton-finance-amount-bar-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.skeleton-finance-icon {
  margin-right: 8px;
  display: flex;
  align-items: center;
  animation: skeleton-icon-float 1.2s infinite alternate, skeleton-icon-fade 1.2s infinite alternate;
}
@keyframes skeleton-icon-float {
  0% { transform: translateY(0); }
  100% { transform: translateY(-8px); }
}
@keyframes skeleton-icon-fade {
  0% { opacity: 0.7; }
  100% { opacity: 1; }
}
.skeleton-finance-amount-bar {
  display: inline-block;
  width: 90px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(90deg, #e3f0dd 25%, #b2dfdb 50%, #e3f0dd 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite linear;
  box-shadow: 0 2px 8px #b2dfdb33;
}
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>